import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Chart, ScatterController, LineController,
  LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from "chart.js";

// ── Quadratic least-squares fit y = ax² + bx + c ──────────────────────────────
function fitQuadratic(pts) {
  if (pts.length < 3) return null;
  const n = pts.length;
  let s0=n,s1=0,s2=0,s3=0,s4=0,t0=0,t1=0,t2=0;
  pts.forEach(({x,y}) => { s1+=x;s2+=x*x;s3+=x*x*x;s4+=x*x*x*x;t0+=y;t1+=x*y;t2+=x*x*y; });
  const A=[[s0,s1,s2],[s1,s2,s3],[s2,s3,s4]], B=[t0,t1,t2];
  for (let i=0;i<3;i++) {
    let mx=i; for(let k=i+1;k<3;k++) if(Math.abs(A[k][i])>Math.abs(A[mx][i])) mx=k;
    [A[i],A[mx]]=[A[mx],A[i]]; [B[i],B[mx]]=[B[mx],B[i]];
    if(Math.abs(A[i][i])<1e-12) return null;
    for(let k=i+1;k<3;k++){const f=A[k][i]/A[i][i];for(let j=i;j<3;j++)A[k][j]-=f*A[i][j];B[k]-=f*B[i];}
  }
  const v=[0,0,0];
  for(let i=2;i>=0;i--){v[i]=B[i]/A[i][i];for(let k=i-1;k>=0;k--)B[k]-=A[k][i]*v[i];}
  const [c,b,a]=v;
  return (isNaN(a)||isNaN(b)||isNaN(c)||a>=0) ? null : {a,b,c};
}

// Catmull-Rom spline through sorted points — used when only 2 points (no quadratic)
function catmullRomPoints(pts, steps=200) {
  if (pts.length < 2) return pts;
  const sorted = [...pts].sort((a,b)=>a.x-b.x);
  const p = [
    {x: sorted[0].x - (sorted[1].x-sorted[0].x), y: sorted[0].y},
    ...sorted,
    {x: sorted[sorted.length-1].x + (sorted[sorted.length-1].x-sorted[sorted.length-2].x), y: sorted[sorted.length-1].y},
  ];
  const result = [];
  for (let seg=1; seg<p.length-2; seg++) {
    const [p0,p1,p2,p3] = [p[seg-1],p[seg],p[seg+1],p[seg+2]];
    const segSteps = Math.ceil(steps / (p.length-3));
    for (let i=0; i<=segSteps; i++) {
      const t = i/segSteps;
      const t2=t*t, t3=t2*t;
      const x = 0.5*((2*p1.x)+(-p0.x+p2.x)*t+(2*p0.x-5*p1.x+4*p2.x-p3.x)*t2+(-p0.x+3*p1.x-3*p2.x+p3.x)*t3);
      const y = 0.5*((2*p1.y)+(-p0.y+p2.y)*t+(2*p0.y-5*p1.y+4*p2.y-p3.y)*t2+(-p0.y+3*p1.y-3*p2.y+p3.y)*t3);
      result.push({x:+x.toFixed(4), y:+y.toFixed(4)});
    }
  }
  return result;
}

function buildQuadCurve(coeff, xMin, xMax, steps=300) {
  const pts = [];
  for (let i=0;i<=steps;i++) {
    const x = xMin + (i/steps)*(xMax-xMin);
    pts.push({x:+x.toFixed(4), y:+(coeff.a*x*x+coeff.b*x+coeff.c).toFixed(4)});
  }
  return pts;
}

function getPeak(coeff) {
  if (!coeff) return null;
  const x = -coeff.b/(2*coeff.a);
  const y = coeff.a*x*x+coeff.b*x+coeff.c;
  return {x,y};
}

function getSplinePeak(pts) {
  if (!pts.length) return null;
  return pts.reduce((best,p) => p.y > best.y ? p : best, pts[0]);
}

// ── Peak annotation plugin ─────────────────────────────────────────────────────
function makePeakPlugin(peaks) {
  return {
    id: "peakAnnotations",
    afterDatasetsDraw(chart) {
      const { ctx, scales: { x: xs, y: ys }, chartArea: ca } = chart;
      if (!xs || !ys || !ca) return;

      const resolved = peaks.map(({ peak, color, label }) => {
        if (!peak) return null;
        const px = xs.getPixelForValue(peak.x);
        const py = ys.getPixelForValue(peak.y);
        if (px < ca.left-2 || px > ca.right+2 || py < ca.top-2 || py > ca.bottom+2) return null;
        ctx.font = "bold 10px sans-serif";
        const boxW = Math.max(...label.map(l => ctx.measureText(l).width)) + 18;
        const boxH = label.length * 14 + 14;
        return { px, py, color, label, boxW, boxH, peak };
      }).filter(Boolean);

      const GAP = 8;
      resolved.forEach(r => {
        r.bx = Math.max(ca.left + 2, Math.min(r.px - r.boxW / 2, ca.right - r.boxW - 2));
        r.by = r.py - r.boxH - 22;
        if (r.by < ca.top + 4) r.by = r.py + 22;
      });

      resolved.sort((a, b) => a.px - b.px);

      if (resolved.length === 2) {
        const [L, R] = resolved;
        for (let iter = 0; iter < 12; iter++) {
          const xOverlap = (L.bx + L.boxW + GAP) - R.bx;
          if (xOverlap <= 0) break;
          const yOverlap = Math.min(
            (L.by + L.boxH + GAP) - R.by,
            (R.by + R.boxH + GAP) - L.by
          );
          if (yOverlap <= 0) break;
          const push = xOverlap / 2 + 1;
          L.bx = Math.max(ca.left + 2, L.bx - push);
          R.bx = Math.min(ca.right - R.boxW - 2, R.bx + push);
        }
        const xStillOverlap = (L.bx + L.boxW + GAP) - R.bx;
        const yStillOverlap = Math.min(
          (L.by + L.boxH + GAP) - R.by,
          (R.by + R.boxH + GAP) - L.by
        );
        if (xStillOverlap > 0 && yStillOverlap > 0) {
          const [upper, lower] = L.py <= R.py ? [L, R] : [R, L];
          upper.by = Math.max(ca.top + 2, lower.by - upper.boxH - GAP);
        }
      }

      resolved.forEach(r => {
        const { px, py, color, label, boxW, boxH } = r;
        let { bx, by } = r;
        ctx.save();

        ctx.setLineDash([5, 4]);
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = color + "99";
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, ca.bottom); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(ca.left, py);   ctx.stroke();
        ctx.setLineDash([]);

        ctx.font = "bold 9px sans-serif";
        ctx.textAlign = "center"; ctx.textBaseline = "top";
        const xLabel = r.peak.x.toFixed(2) + "%";
        const xLW = ctx.measureText(xLabel).width + 10;
        ctx.fillStyle = color;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(px - xLW/2, ca.bottom + 3, xLW, 14, 3);
        else ctx.rect(px - xLW/2, ca.bottom + 3, xLW, 14);
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.fillText(xLabel, px, ca.bottom + 5.5);

        ctx.textAlign = "right"; ctx.textBaseline = "middle";
        const yLabel = r.peak.y.toFixed(2);
        const yLW = ctx.measureText(yLabel).width + 10;
        ctx.fillStyle = color;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(ca.left - yLW - 3, py - 7, yLW, 14, 3);
        else ctx.rect(ca.left - yLW - 3, py - 7, yLW, 14);
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.fillText(yLabel, ca.left - 5, py);

        ctx.fillStyle = color;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(bx, by, boxW, boxH, 6);
        else ctx.rect(bx, by, boxW, boxH);
        ctx.fill();

        const tipX = Math.min(Math.max(px, bx + 10), bx + boxW - 10);
        const boxAbovePeak = (by + boxH) < py;
        ctx.beginPath();
        if (boxAbovePeak) {
          ctx.moveTo(tipX - 6, by + boxH);
          ctx.lineTo(tipX + 6, by + boxH);
          ctx.lineTo(tipX, by + boxH + 9);
        } else {
          ctx.moveTo(tipX - 6, by);
          ctx.lineTo(tipX + 6, by);
          ctx.lineTo(tipX, by - 9);
        }
        ctx.closePath(); ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "center"; ctx.textBaseline = "top";
        ctx.font = "bold 10px sans-serif";
        label.forEach((line, i) => ctx.fillText(line, bx + boxW/2, by + 7 + i * 14));

        ctx.beginPath(); ctx.arc(px, py, 9,   0, Math.PI*2); ctx.fillStyle = "#ffffff"; ctx.fill();
        ctx.beginPath(); ctx.arc(px, py, 6.5, 0, Math.PI*2); ctx.fillStyle = color;    ctx.fill();
        ctx.beginPath(); ctx.arc(px, py, 2.5, 0, Math.PI*2); ctx.fillStyle = "#ffffff"; ctx.fill();

        ctx.restore();
      });
    },
  };
}

// ── Register built-ins + dark background ──────────────────────────────────────
Chart.register(
  ScatterController, LineController, LinearScale,
  PointElement, LineElement, Title, Tooltip, Legend, Filler,
  {
    id: "darkBg",
    beforeDraw(chart) {
      const ctx = chart.canvas.getContext("2d");
      ctx.save();
      ctx.globalCompositeOperation = "destination-over";
      ctx.fillStyle = "#0f172a";
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  }
);

// ── Core chart builder — shared by component and image export ──────────────────
function renderProctorChart(canvas, trials, extraOpts = {}) {
  const valid = (trials||[]).filter(t => t && !isNaN(t.moisture) && !isNaN(t.dryDensity) && !isNaN(t.ddCorr));
  if (valid.length < 2) return null;

  const ptU = valid.map(t=>({x:t.moisture, y:t.dryDensity}));
  const ptC = valid.map(t=>({x:t.moisture, y:t.ddCorr}));

  const coeffU = fitQuadratic(ptU);
  const coeffC = fitQuadratic(ptC);

  const allX = valid.map(t=>t.moisture);
  const allY = [...valid.map(t=>t.dryDensity), ...valid.map(t=>t.ddCorr)];
  const xRange = Math.max(...allX)-Math.min(...allX) || 4;
  const yRange = Math.max(...allY)-Math.min(...allY) || 6;
  const xMin = Math.min(...allX) - Math.max(xRange*1.20, 8);
  const xMax = Math.max(...allX) + Math.max(xRange*1.20, 8);
  const yMin = Math.min(...allY) - Math.max(yRange*0.80, 8);
  const yMax = Math.max(...allY) + Math.max(yRange*0.80, 10);

  const curveU = coeffU ? buildQuadCurve(coeffU, xMin, xMax, 300) : catmullRomPoints(ptU, 200);
  const curveC = coeffC ? buildQuadCurve(coeffC, xMin, xMax, 300) : catmullRomPoints(ptC, 200);

  const peakU = coeffU ? getPeak(coeffU) : getSplinePeak(curveU);
  const peakC = coeffC ? getPeak(coeffC) : getSplinePeak(curveC);

  const peakPlugin = makePeakPlugin([
    peakU ? {
      peak: peakU, color: "#3b82f6",
      label: [`OMC = ${peakU.x.toFixed(2)}%`, `MDD = ${peakU.y.toFixed(2)} pcf`],
    } : null,
    peakC ? {
      peak: peakC, color: "#ef4444",
      label: [`OMC(corr) = ${peakC.x.toFixed(2)}%`, `MDD(corr) = ${peakC.y.toFixed(2)} pcf`],
    } : null,
  ].filter(Boolean));

  return new Chart(canvas, {
    type: "scatter",
    plugins: [peakPlugin],
    data: {
      datasets: [
        {
          label: "Uncorrected Curve",
          data: curveU,
          type: "line",
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59,130,246,0.08)",
          fill: false,
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0,
          order: 4,
          parsing: false,
        },
        {
          label: "Corrected Curve",
          data: curveC,
          type: "line",
          borderColor: "#ef4444",
          backgroundColor: "transparent",
          fill: false,
          borderWidth: 2.5,
          borderDash: [8, 4],
          pointRadius: 0,
          tension: 0,
          order: 5,
          parsing: false,
        },
        {
          label: "Uncorrected Points",
          data: ptU,
          type: "scatter",
          backgroundColor: "#3b82f6",
          borderColor: "#ffffff",
          borderWidth: 2,
          pointRadius: 7,
          pointHoverRadius: 9,
          order: 1,
        },
        {
          label: "Corrected Points",
          data: ptC,
          type: "scatter",
          backgroundColor: "#ef4444",
          borderColor: "#ffffff",
          borderWidth: 2,
          pointRadius: 7,
          pointHoverRadius: 9,
          order: 2,
        },
      ],
    },
    options: {
      animation: { duration: 600, easing: "easeInOutQuart" },
      responsive: true,
      maintainAspectRatio: true,
      layout: { padding: { top: 12, right: 28, bottom: 36, left: 14 } },
      scales: {
        x: {
          type: "linear", min: xMin, max: xMax,
          title: {
            display: true, text: "Moisture Content (%)",
            color: "#94a3b8", font: { size: 12, weight: "600" },
            padding: { top: 12 },
          },
          grid: { color: "rgba(255,255,255,0.05)", lineWidth: 1 },
          ticks: {
            color: "#94a3b8", font: { size: 11 },
            callback: v => v.toFixed(1)+"%",
            maxTicksLimit: 10,
            padding: 6,
          },
          border: { color: "rgba(255,255,255,0.15)" },
        },
        y: {
          type: "linear", min: yMin, max: yMax,
          title: {
            display: true, text: "Dry Density (pcf)",
            color: "#94a3b8", font: { size: 12, weight: "600" },
            padding: { bottom: 8 },
          },
          grid: { color: "rgba(255,255,255,0.05)", lineWidth: 1 },
          ticks: {
            color: "#94a3b8", font: { size: 11 },
            callback: v => v.toFixed(1),
            maxTicksLimit: 8,
            padding: 6,
          },
          border: { color: "rgba(255,255,255,0.15)" },
        },
      },
      plugins: {
        title: {
          display: true,
          text: "Proctor Curve — Dry Density vs Moisture Content",
          color: "#e2e8f0",
          font: { size: 14, weight: "700" },
          padding: { bottom: 16 },
        },
        legend: {
          position: "top",
          labels: {
            color: "#cbd5e1", font: { size: 11 },
            usePointStyle: true, padding: 20,
            filter: item => item.text.includes("Points"),
          },
        },
        tooltip: {
          backgroundColor: "#1e293b",
          titleColor: "#e2e8f0",
          bodyColor: "#cbd5e1",
          borderColor: "#334155",
          borderWidth: 1,
          padding: 10,
          filter: item => item.dataset.pointRadius > 0,
          callbacks: {
            title: ctx => ctx.length ? `Trial ${ctx[0].dataIndex + 1}` : "",
            label: ctx => {
              const d = ctx.raw;
              return [
                ` Moisture:    ${(+d.x).toFixed(2)} %`,
                ` Dry Density: ${(+d.y).toFixed(2)} pcf`,
              ];
            },
          },
        },
      },
      ...extraOpts,
    },
  });
}

// ── Off-screen chart image generator — for PDF/Excel export without mounted component
export function generateProctorChartImage(trials, width = 900, height = 450) {
  return new Promise(resolve => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    // Must be in the DOM for Chart.js to render correctly
    canvas.style.position = "fixed";
    canvas.style.top = "-9999px";
    canvas.style.left = "-9999px";
    canvas.style.visibility = "hidden";
    document.body.appendChild(canvas);

    const chart = renderProctorChart(canvas, trials, {
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
    });

    if (!chart) {
      document.body.removeChild(canvas);
      resolve(null);
      return;
    }

    // Double rAF ensures Chart.js has flushed its render to the canvas
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const off = document.createElement("canvas");
      off.width = width; off.height = height;
      const ctx = off.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, off.width, off.height);
      ctx.drawImage(canvas, 0, 0);
      const url = off.toDataURL("image/png");
      chart.destroy();
      document.body.removeChild(canvas);
      resolve(url);
    }));
  });
}

// ── Component ──────────────────────────────────────────────────────────────────
const ProctorChart = forwardRef(function ProctorChart({ trials }, ref) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useImperativeHandle(ref, () => ({
    getImageDataURL: () => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const off = document.createElement("canvas");
      off.width = canvas.width; off.height = canvas.height;
      const ctx = off.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, off.width, off.height);
      ctx.drawImage(canvas, 0, 0);
      return off.toDataURL("image/png");
    },
  }));

  const trialsKey = JSON.stringify(trials);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

    chartRef.current = renderProctorChart(canvas, trials);

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [trialsKey]); // eslint-disable-line

  return (
    <div style={{
      background: "#0f172a",
      border: "1px solid #1e3a5f",
      borderRadius: 12,
      padding: "20px 20px 16px",
      marginTop: 24,
    }}>
      <canvas ref={canvasRef} style={{ width: "100%", maxHeight: 440 }} />
    </div>
  );
});

export default ProctorChart;
export { fitQuadratic };
export const buildCurvePoints = buildQuadCurve;
